import { DSIconColors } from '@elliemae/ds-icon';
import { InfoCircle } from '@elliemae/ds-icons';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import { calculateTotalLoanAmount, useLookup } from '@frontend/pricing-search';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { currencyWithDecimals } from '../../../../common/utils/shared';
import {
  useLoanDataSelector,
  useLoanInformationField,
  useOriginalLoanInformationSelector,
} from '../../../../data/useSelectors';
import { useGridPreferences } from '../ClassicGrid/gridPreferences';
import {
  StyledBanner,
  StyledHeader,
  StyledParam,
  StyledParams,
} from './styled';

const EMPTY = '---';

// Mirrors how the Encompass ribbon resolves these codes
const lookupLabel = (lookup: any, code: unknown): string => {
  if (!Array.isArray(lookup)) return EMPTY;
  const match = lookup.find((entry) => entry?.value === code);
  return match?.key || match?.label || EMPTY;
};

// currencyWithDecimals returns an empty string for a missing amount.
const currency = (value: unknown): string =>
  currencyWithDecimals(value, 2) || EMPTY;

// Matches the search form's Effective Date field.
const EFFECTIVE_DATE_FORMAT = 'MM/DD/YYYY hh:mm A';

// Declared because the raw value is not always ISO. ET to agree with
// PricingNotificationHeader, which renders the same value on this page.
const EFFECTIVE_DATE_INPUTS = [
  'MM/DD/YYYY hh:mm A',
  'MM/DD/YYYY hh:mm:ss A',
  'YYYY-MM-DDTHH:mm:ss a z',
];

const effectiveDateText = (value: unknown): string => {
  if (!value) return EMPTY;
  const parsed = moment.tz(
    String(value),
    EFFECTIVE_DATE_INPUTS,
    'America/New_York',
  );
  return parsed.isValid() ? parsed.format(EFFECTIVE_DATE_FORMAT) : EMPTY;
};

const Param = ({ label, value }: { label: string; value: string }) => (
  <StyledParam>
    <DSTypography
      variant={TYPOGRAPHY_VARIANTS.B1}
      fontSize="12px"
      color="brand-200"
    >
      {label}
    </DSTypography>
    <DSTypography
      variant={TYPOGRAPHY_VARIANTS.B1}
      fontSize="12px"
      fontWeight={600}
      color="brand-100"
    >
      {value}
    </DSTypography>
  </StyledParam>
);

const BannerBody = () => {
  const loanData = useLoanDataSelector() as any;
  const originalLoanInformation = useOriginalLoanInformationSelector() as any;
  const firstMortgageAmount = useLoanInformationField('firstMortgageAmount', 0);
  const financedAmount = useLoanInformationField('financedAmount', 0);

  const purposeLookup = useLookup('loanUsage');
  const propertyTypeLookup = useLookup('propertyTypes');
  const propertyUseLookup = useLookup('propertyUse');

  const { property, borrowers, loanInformation, relockRequest } =
    loanData || {};

  const location = [get(property, 'state'), get(property, 'city')]
    .filter(Boolean)
    .join(' - ');
  const fico = get(borrowers, '[0].borrowerFinanancial.creditScore');
  // Only set for relock and historical pricing searches.
  const effectiveDate = get(relockRequest, 'effectiveDate');
  const ltv = get(loanInformation, 'ltv') ?? originalLoanInformation?.ltv;

  return (
    <StyledBanner data-testid="search-parameters-banner">
      <StyledHeader>
        <InfoCircle size="s" color={DSIconColors.WHITE} />
        <DSTypography
          variant={TYPOGRAPHY_VARIANTS.B1}
          fontSize="12px"
          color="brand-200"
        >
          Parameters used in Search
        </DSTypography>
      </StyledHeader>

      <StyledParams data-testid="search-parameters">
        <Param
          label="Purpose"
          value={lookupLabel(
            purposeLookup,
            get(loanInformation, 'loanPurpose'),
          )}
        />
        <Param
          label="Loan Amount"
          value={currency(
            calculateTotalLoanAmount(firstMortgageAmount, financedAmount),
          )}
        />
        <Param label="Location" value={location || EMPTY} />
        <Param label="FICO" value={fico ? String(fico) : EMPTY} />
        <Param
          label="Property Value"
          value={currency(get(property, 'value'))}
        />
        <Param
          label="Property Type"
          value={lookupLabel(propertyTypeLookup, get(property, 'type'))}
        />
        <Param
          label="Property Use"
          value={lookupLabel(propertyUseLookup, get(property, 'use'))}
        />
        <Param
          label="Effective Date"
          value={effectiveDateText(effectiveDate)}
        />
        <Param
          label="LTV"
          value={ltv != null ? Number(ltv).toFixed(2) : EMPTY}
        />
      </StyledParams>
    </StyledBanner>
  );
};

// Split so the lookups only run while the banner is switched on, which is not
// the default.
export const SearchParametersBanner = () => {
  const { preferences } = useGridPreferences();

  if (!preferences.displaySearchCriteria) return null;
  return <BannerBody />;
};
